import type { Person } from './types.js';
import { round2 } from './units.js';

export const MAX_SPAN_DAYS = 3;

/**
 * Adult-equivalent portions for the household.
 *
 * This is the single most load-bearing number in the app. For the Eden household
 * it is 3.1, NOT 5 — two adults at 1.0, a five-year-old at 0.5, and 2.5-year-old
 * twins at 0.3 each. Treating five people as five portions over-buys by ~40%
 * every week. Never hard-code the result; always derive it from `people`.
 */
export function householdPortions(people: Person[]): number {
  let total = 0;
  for (const p of people) {
    if (!Number.isFinite(p.portionFactor)) {
      throw new Error(
        `Person "${p.id}" has a non-finite portion factor: ${p.portionFactor}. ` +
        `NaN or Infinity would poison every quantity in the app without raising anything.`,
      );
    }
    if (p.portionFactor < 0) {
      throw new Error(`Person "${p.id}" has a negative portion factor: ${p.portionFactor}`);
    }
    total += p.portionFactor;
  }
  return round2(total);
}

/** Portions to cook for one dinner slot, accounting for leftovers and batch cooks. */
export function dinnerPortions(household: number, spansDays: number): number {
  if (!Number.isInteger(spansDays) || spansDays < 1 || spansDays > MAX_SPAN_DAYS) {
    throw new Error(`spansDays must be an integer between 1 and ${MAX_SPAN_DAYS}, got ${spansDays}`);
  }
  return round2(household * spansDays);
}
